import { IRestfulAction, IRestfulConfigMap } from ".";
import { IResponse, IRetryParameter, IResponseEvent, IError } from "../model";
import { IDataAccessorObjectTree } from "../object-tree";
import { UrlUtil } from "../../utils/url-util";
import { IErrorConfig, ILogger } from "../../logger/types";
import { envConfig } from "@env/env-config";
import { number } from "@core/utils/number";

export class HttpHandler {

    private SERVICE_CONFIGS: IRestfulConfigMap;

    private RETRY_MAX_TIME: number;

    private RETRY_TIME_SPAN: number;

    private DEFAULT_HEADERS: angular.IHttpRequestConfigHeaders;

    constructor(
        private $http: angular.IHttpService,
        private $q: angular.IQService,
        private $timeout: angular.ITimeoutService,
        private $objectTree: IDataAccessorObjectTree,
        private $cacheFactory: angular.ICacheFactoryService,
        private $logger?: ILogger,
    ) {
        this.promiseSuccess = this.promiseSuccess.bind(this);
        this.promiseError = this.promiseError.bind(this);
        this.promiseFailed = this.promiseFailed.bind(this);
        this.downloadFileByBlob = this.downloadFileByBlob.bind(this);
        this.readJsonByBlob = this.readJsonByBlob.bind(this);
        this.retryPromise = this.retryPromise.bind(this);
        this.httpRetry.bind(this);
    }

    public init(serviceConfigs: IRestfulConfigMap, retryMaxTime: number, retryTimeSpan: number, defaultHeaders: angular.IHttpRequestConfigHeaders) {
        this.SERVICE_CONFIGS = serviceConfigs;
        this.RETRY_MAX_TIME = retryMaxTime;
        this.RETRY_TIME_SPAN = retryTimeSpan
        this.DEFAULT_HEADERS = defaultHeaders;
    }

    private sendError(moduleName: string, e: IResponseEvent<any>) {
        if (this.$logger != undefined) {
            let err = new Error();

            err.name = moduleName;
            err.message = e?.data?.data?.message || "";

            let ctx = {
                url: e.config.url,
                body: e.config.data,
                params: e.config.params,
                method: e.config.method
            }

            let obj: IErrorConfig = {
                title: "请求接口错误--" + err.message,
                module: moduleName,
                context: ctx,
                error: err
            };

            this.$logger.error(obj);
        }
    }

    /**
       * @function restService.promiseSuccess
       * @description 调用promise成功返回时处理
       * 
       * @param {*} event 
       * @param {*} deferred 
       */
    private promiseSuccess<T>(event: IResponseEvent<T>, deferred: angular.IDeferred<IResponse<T>>): void {
        (function (e: IResponseEvent<T>) {
            if (this.isApiResponseSuccess(e)) {
                deferred.resolve(e.data || e);
            } else if (e.config.responseType == "blob") {
                deferred.resolve(e.data || e);
            } else {
                this.promiseFailed(e, deferred);
            }
        }).bind(this)(event);
    }

    /**
     * @function restService.promiseError
     * @description 调用promise出错时处理
     * 
     * @param {*} event 
     * @param {*} deferred 
     */
    private promiseError<T>(event: IResponseEvent<T>, deferred: angular.IDeferred<IResponse<T>>): void {
        // // 抓取请求失败错误手动发送sentry
        this.sendError("RESTFUL_REJECT", event);

        this.promiseFailed(event, deferred);
    }

    /**
     * @function restService.promiseError
     * @description 调用promise返回失败时处理
     * 
     * @param {*} event 
     * @param {*} deferred 
     */
    private promiseFailed<T>(event: IResponseEvent<T>, deferred: angular.IDeferred<IResponse<T>>): void {
        (function (e) {
            deferred.reject(e);
        })(event);
    }

    /**
     * 通过Blob下载文件
     * @param {*} event 
     */
    private downloadFileByBlob(event: angular.IHttpResponse<any>) {
        let blob = new Blob([event.data], {
            type: event.data.type
        });
        let reader = new FileReader();
        reader.readAsBinaryString(blob);

        let downloadElement = document.createElement('a');
        downloadElement.href = window.URL.createObjectURL(blob);

        let fileName = "";
        //服务器返回了指定文件名
        if (event.headers("content-disposition") && /filename=([\w-.%]*)/i.test(event.headers("content-disposition"))) {
            let names = event.headers("content-disposition").match(/filename=([\w-.%]*)/i);

            if (names && names.length > 1) {
                fileName = window.decodeURIComponent(names[1]);
            }
        }

        //如果服务器没有指定文件名，则生成时间有关的随机名称
        if (fileName == "") {
            let now = new Date();
            let fileFormat = "txt";
            let formats = event.headers("content-type").match(/(application|image)\/([\w|\w\+\w]*)(;|$)/i);
            if (formats && formats.length > 2) {
                fileFormat = formats[2];
            }

            // if (fileFormat == "octet-stream") {
            //     event.headers("content-disposition") == "image/svg+xml";
            //     fileFormat = "svg";
            // }

            if (fileFormat == "svg+xml") {
                fileFormat = "svg";
            }

            fileName = now.getFullYear().toString() + "-" + (now.getMonth() + 1).toString() + "-" + now.getDate() + "_" + number.random(0, 9999, true) + "." + fileFormat;
        }

        downloadElement.download = fileName;
        document.body.appendChild(downloadElement);
        downloadElement.click();
        downloadElement.remove();
        window.URL.revokeObjectURL(downloadElement.href);
    }

    /**
     * 通过Blob读取JSON对象
     * @param {*} event 
     */
    private readJsonByBlob(event: angular.IHttpResponse<any>, callback?: Function) {
        let blob = new Blob([event.data], {
            type: event.data.type
        });
        let reader = new FileReader();
        reader.readAsBinaryString(blob);

        reader.onload = function () {
            //是否需要retry
            let result = reader.result as string;
            let resultToJson = JSON.parse(result);
            callback && callback(resultToJson);
        }
    }

    /**
     * @function restService.promiseRetry
     * @description 调用promise成功返回时处理
     * @param {*} obj 
     * @param {*} excuteTimes 
     * @param {*} event 
     * @param {*} deferred 
     * @param {*} retryFunc 
     */
    private retryPromise<T>(httpConfig: angular.IRequestConfig, event: IResponseEvent<T>, deferred: angular.IDeferred<IResponse<T>>, retryParameter: IRetryParameter) {
        if (this.isApiResponseSuccess(event)) {
            this.promiseSuccess(event, deferred);
        } else {
            //需要重试
            if (retryParameter.needRetry || event.needRetry || (event.data && event.data.needRetry)) {
                this.needRetryFn(httpConfig, event, deferred, retryParameter);
            } else {
                this.promiseFailed(event, deferred);
            }
        }
    }

    /**
     * cgp等api请求是否成功
     * @param event 
     * @returns 
     */
    private isApiResponseSuccess<T>(e: IResponseEvent<T>): boolean {
        if (e != undefined || e != null) {
            if (e.success || e.Success || (e.data && (e.data.success || e.data.Success))) {
                return true;
            } else if (angular.isArray(e.data) && e.data.length > 0) {
                return true;
            }
        }

        return false;
    }

    /**
     * 重试函数
     * @param event 
     * @returns 
     */
    private needRetryFn<T>(httpConfig: angular.IRequestConfig, event: IResponseEvent<T>, deferred: angular.IDeferred<IResponse<T>>, retryParameter: IRetryParameter) {
        //未达到重试次数上限
        if (retryParameter.retryMaxtime && retryParameter.retryMaxtime > retryParameter.excuteTimes) {
            this.$timeout(function () {
                retryParameter.excuteTimes += 1;
                retryParameter.retryFunc(httpConfig, deferred, retryParameter);
            }, retryParameter.retryTimeSpan)
        } else {
            //达到重试次数上限
            this.promiseError(event, deferred);
            // 通知用户并询问是否重试
        }
    }

    /**
     * @function restService.httpRetry
     * @description 生成http请求的Promise
     * 
     * @param {*} obj 
     * @param {Number} excuteTimes
     * @param {*} deferred 
     * @param {*} successFunc 
     * @param {*} errorFunc 
     */
    private httpRetry<T>(httpConfig: angular.IRequestConfig, deferred: angular.IDeferred<IResponse<T>>, retryParameter: IRetryParameter) {
        this.$http(httpConfig).then(
            //成功返回处理函数
            function (event: IResponseEvent<T>) {
                if (event && event.config && event.config.responseType === "blob") {
                    if (/(application\/json)/i.test(event.headers("content-type"))) {
                        this.readJsonByBlob(event, (data) => {
                            event.data = data;
                            this.retryPromise(httpConfig, event, deferred, retryParameter);
                        });
                    } else {
                        this.downloadFileByBlob(event);
                        this.promiseSuccess(event, deferred);
                    }
                } else {
                    this.retryPromise(httpConfig, event, deferred, retryParameter);
                }
            }.bind(this),
            //调用失败时的处理函数
            function (event) {
                // 401无权限时则不重试
                if (event.status == 401) {
                    this.promiseFailed(event, deferred);
                } else {
                    if (retryParameter.needRetry || event.needRetry || (event.data && event.data.needRetry)) {
                        this.needRetryFn(httpConfig, event, deferred, retryParameter);
                    } else {
                        this.promiseError(event, deferred);
                    }
                }
            }.bind(this)
        );
    }

    private getCacheFactory(serviceName: string): angular.ICacheObject {
        if (this.$cacheFactory.get(serviceName)) {
            return this.$cacheFactory.get(serviceName);
        } else {
            return this.$cacheFactory(serviceName);
        }
    }

    /**
     * http请求
     * @function restService.http
     * @param serviceName 
     * @param action 
     * @param config 
     * @returns 
     */
    public http<T>(serviceName: string, action: string): angular.IPromise<IResponse<T>>;
    public http<T, S>(serviceName: string, action: string, config: { parameter: S, headers?: angular.IHttpRequestConfigHeaders }): angular.IPromise<IResponse<T>>;
    public http<T, S>(serviceName: string, action: string, config: { data: S, headers?: angular.IHttpRequestConfigHeaders }): angular.IPromise<IResponse<T>>;
    public http<T, R, S>(serviceName: string, action: string, config?: { parameter?: S, data?: R, headers?: angular.IHttpRequestConfigHeaders }): angular.IPromise<IResponse<T>>;
    public http<T, R, S>(serviceName: string, action: string, config?: { parameter?: S, data?: R, headers?: angular.IHttpRequestConfigHeaders }): angular.IPromise<IResponse<T>> {

        //http请求的配置对象
        let cfg = this.SERVICE_CONFIGS[serviceName.toLowerCase()];
        if (!cfg) {
            throw new Error("missing service config:" + serviceName);
        }

        if (!cfg.actions) {
            throw new Error("missing actions config in " + serviceName);
        }

        let actionCfg: IRestfulAction | undefined;

        for (let k in Object.assign(cfg.actions)) {
            if (action.toLowerCase() == k.toLowerCase()) {
                actionCfg = cfg.actions[k];
            }
        }

        if (!actionCfg) {
            throw new Error("missing action config '" + action + "' in " + serviceName);
        }

        //构建URL参数
        let path = this.$objectTree.useTestVersion && actionCfg.testPath ? actionCfg.testPath : actionCfg.path;

        // actionCfg.paramDefaults:URL参数
        if (actionCfg.paramDefaults) {
            if (config) {
                if (config.parameter) {
                    path = UrlUtil.setUrlParams(path, actionCfg.paramDefaults, config.parameter);
                } else if (config.data) {
                    path = UrlUtil.setUrlParams(path, actionCfg.paramDefaults, config.data);
                }
            }
        }

        let url = UrlUtil.joinUrl([cfg.appPath, path]);

        //构建$http参数
        let httpCfg: angular.IRequestConfig = {
            url: url,
            method: actionCfg.method,
            // headers: this.DEFAULT_HEADERS
            headers: angular.copy(this.DEFAULT_HEADERS)
        };

        if (config) {
            if (config.data) {
                httpCfg.data = config.data;
            }
            if (config.headers) {
                httpCfg.headers = angular.extend({}, httpCfg.headers, config.headers);
            }
        }

        if (actionCfg.requestConfig) {
            angular.merge(httpCfg, actionCfg.requestConfig);
        }

        //是否需要校验
        let isAuth = actionCfg.isAuth;

        if (isAuth === undefined) {
            isAuth = (cfg.isAuth === undefined ? true : cfg.isAuth);
        }

        //是否需要认证
        if (isAuth && this.$objectTree.token) {
            angular.merge(httpCfg, {
                "headers": {
                    "Authorization": this.$objectTree.token.token_type + " " + this.$objectTree.token.access_token
                }
            });
        }

        // 是否需要currencyCode
        if (actionCfg.needCurrency) {
            //objectTree.currencyCode|defaultCurrencyCode
            let currentCurrencyCode = this.$objectTree.getCurrentPreference().currencyCode;
            let currencyCode = currentCurrencyCode || envConfig.defaultCurrency();
            
            angular.merge(httpCfg, {
                "headers": {
                    "currencyCode": currencyCode
                }
            });
        }
        
        // 是否需要language
        // if (actionCfg.needLanguage) {
        //     //objectTree.language|defaultLanguage
        //     angular.merge(httpCfg, {
        //         "headers": {
        //             "Language": language
        //         }
        //     });
        // }

        let deferred = this.$q.defer<IResponse<T>>();

        // TODO: cache与retry同时存在的情况
        /**
         * @description: http cache缓存
         */
        let cache = actionCfg.cache;
        if (cache != undefined || cache != null) {
            let cfgServiceName = serviceName.toLowerCase();

            if (!cache.cacheId) {
                throw new Error("missing cahce config in " + serviceName);
            }

            let { cacheId } = cache;
            let needParameterReplace: boolean = (/^@/).test(cacheId);
            let cacheFactory: angular.ICacheObject = this.getCacheFactory(cfgServiceName);

            // cacheId替换
            // (只有get请求才做缓存)
            if ((httpCfg.method).toLowerCase() == "get" && needParameterReplace) {
                if (config && config.parameter) {
                    for (let key in Object.assign(config.parameter as {})) {
                        if (Object.prototype.hasOwnProperty.call(config.parameter, key)) {
                            const value = config.parameter[key];

                            if (RegExp("@" + key).test(cacheId)) {
                                cacheId = cache.combineKey
                                    ? cacheId.replace("@" + key, "$" + cache.combineKey + "$" + value)
                                    : cacheId.replace("@" + key, key + ":" + value + "$");
                            }
                        }
                    }
                }
            }

            if (cacheFactory.get(cacheId)) { // cacheFactory已有缓存id
                deferred.resolve(cacheFactory.get(cacheId));
            } else { // cacheFactory未有缓存id
                this.$http(httpCfg).then((event: IResponseEvent<T>) => {
                    if (event.success || event.Success ||
                        (event.data && (event.data.success || event.data.Success))
                    ) {
                        cacheFactory.put(cacheId, event.data);
                        this.promiseSuccess(event, deferred);
                    } else {
                        this.promiseFailed(event, deferred);
                    }
                }, (event: IResponseEvent<T>) => {
                    // 调用失败时的处理函数
                    this.promiseError(event, deferred);
                })
            }

            return deferred.promise;
        }

        let retryParameter: IRetryParameter = {
            excuteTimes: 0,
            retryFunc: this.httpRetry.bind(this)
        };

        if (actionCfg.needRetry) {
            retryParameter.needRetry = true;
            retryParameter.retryMaxtime = actionCfg.retryMaxtime || this.RETRY_MAX_TIME;
            retryParameter.retryTimeSpan = actionCfg.retryTimeSpan || this.RETRY_TIME_SPAN;
        } else {
            retryParameter.retryMaxtime = this.RETRY_MAX_TIME;
            retryParameter.retryTimeSpan = this.RETRY_TIME_SPAN;
        }

        this.httpRetry(httpCfg, deferred, retryParameter);

        return deferred.promise;
    }
}